import api from "./axios";
import {
  ActivationItemGroup,
  ActivationsRequest,
  BaziPredictionRequest,
  BaziResponse,
  ChineseCalendarDate,
  ChineseCalendarDay,
  ICzinResponse,
  QiMenChartResponse,
  RuneOfTheDayResponse,
  TarotDailyPredictionResponse,
  TarotSituationPredictionResponse,
} from "@/types/calculators";
import { ApiResponse } from "@/types/apiResponse";

export async function fetchQiMenChar(
  date: string,
  time: string,
  system: string,
  matrixType: string,
  latitude: number,
  longitude: number,
  timezone: string
): Promise<QiMenChartResponse> {
  const res = await api.get<QiMenChartResponse>(`qimen/${matrixType}`, {
    params: {
      date: date,
      time: time,
      system: system,
      latitude: latitude,
      longitude: longitude,
      timezone: timezone,
    },
  });

  return res.data;
}

export async function fetchICzinPredictions(
  numbers: number[]
): Promise<ICzinResponse> {
  const params = new URLSearchParams();
  numbers.forEach((number) => params.append("coins_sums[]", String(number)));
  const res = await api.get<ApiResponse<ICzinResponse>>(`predictions/iczin`, {
    params,
  });

  return res.data.data;
}

export async function fetchDayRunePredictions(
  dailyRandom: boolean
): Promise<RuneOfTheDayResponse> {
  const params = new URLSearchParams();
  params.set("daily_random", dailyRandom ? "1" : "0");
  const { data } = await api.get("predictions/day-rune", {
    params,
  });

  return data.data;
}

export async function fetchTarotDailyPrediction(): Promise<TarotDailyPredictionResponse> {
  const res = await api.get<ApiResponse<TarotDailyPredictionResponse>>(
    "predictions/day-tarot"
  );

  return res.data.data;
}

export async function fetchTarotSituationPrediction(
  situation: string
): Promise<TarotSituationPredictionResponse> {
  const res = await api.get<ApiResponse<TarotSituationPredictionResponse>>(
    "predictions/tarot-triple-card",
    {
      params: {
        request: situation,
      },
    }
  );

  return res.data.data;
}

export async function fetchAzimuthFacadeAngle(
  lat: string,
  lon: string
): Promise<string> {
  const params = new URLSearchParams();
  params.append("lat", lat);
  params.append("lon", lon);

  const res = await api.get<string>(process.env.VUE_APP_WMM_URL, {
    params: params,
  });

  return res.data.trim();
}

export async function fetchActivations(
  request: ActivationsRequest
): Promise<ActivationItemGroup> {
  const params = new URLSearchParams();
  request.activations.forEach((activation) =>
    params.append("activations[]", activation)
  );
  params.append("date_from", request.dateFrom);
  params.append("date_to", request.dateTo);
  if (request.type === "consultant") {
    params.append("timezone", request.timezone);
    params.append("latitude", request.latitude.toString());
    params.append("longitude", request.longitude.toString());
    if (request.birthday) {
      params.append("birthday", request.birthday);
    }
  }
  if (request.type === "family-member") {
    params.append("family_member_id", request.familyMemberId.toString());
  }

  const res = await api.get<ApiResponse<ActivationItemGroup[]>>("activations", {
    params: params,
  });

  return res.data.data.flat();
}

export async function fetchBaziPrediction(
  request: BaziPredictionRequest
): Promise<BaziResponse> {
  const params = new URLSearchParams();
  params.append("birthday", `${request.date}T${request.time}`);
  params.append("is_male", request.gender);
  params.append("longitude", request.longitude.toString());
  params.append("timezone", request.timezone);

  const res = await api.get("bazi", { params: params });

  return res.data;
}

export async function fetchChineseCalendarMonth(
  dateFrom: string,
  dateTo: string
) {
  const params = new URLSearchParams();

  params.append("date_from", dateFrom);
  params.append("date_to", dateTo);

  const res = await api.get<ApiResponse<ChineseCalendarDate[]>>(
    "chinese-calendar",
    { params: params }
  );

  return res.data.data;
}

export async function fetchChineseCalendarDay(date: string) {
  const params = new URLSearchParams();
  params.append("date", date);

  const res = await api.get<ApiResponse<ChineseCalendarDay>>(
    "chinese-calendar-day",
    { params: params }
  );

  return res.data.data;
}
